import React, { type PropsWithChildren } from 'react';

import { useReportError } from '@atlassian/eoc-global-context';

import { type ErrorBoundaryProps, type ErrorBoundaryState } from '../../common/utils/types';

import DefaultFallbackComponent from './error-boundary-fallback';
import { getErrorId } from './utils';

// eslint-disable-next-line @repo/internal/react/no-class-components
class ErrorBoundaryBase extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	state = {
		error: null,
		hasError: false,
		componentStack: null,
	};

	componentDidCatch(error: unknown, { componentStack }: React.ErrorInfo) {
		this.setState({
			error,
			hasError: true,
			componentStack,
		});
		this.props.onError(error, componentStack);
	}

	componentDidMount() {
		this.props.onMount && this.props.onMount();
	}

	componentWillUnmount() {
		this.props.onUnMount && this.props.onUnMount(this.state.error, this.state.componentStack);
	}

	reset() {
		this.setState({
			error: null,
			hasError: false,
			componentStack: null,
		});
	}

	resetError = () => {
		if (this.props.onReset) {
			this.props.onReset(this.state.error, this.state.componentStack);
		}
		this.reset();
	};

	render(): React.ReactNode {
		const FallbackComponent = this.props.FallbackComponent ?? DefaultFallbackComponent;

		if (this.state.hasError) {
			return (
				<FallbackComponent
					error={this.state.error}
					componentStack={this.state.componentStack}
					resetError={this.resetError}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={this.props.className}
				/>
			);
		}

		return this.props.children;
	}
}

export default function ErrorBoundary({
	children,
	className,
	attributes,
	...props
}: PropsWithChildren<Partial<ErrorBoundaryProps>>) {
	const reporter = useReportError();
	const FallbackComponent = props.FallbackComponent;

	const handleError = (
		error: unknown,
		componentStack: React.ErrorInfo['componentStack'] | null,
	) => {
		if (props.onError) {
			props.onError(error, componentStack);
		}

		const id = getErrorId(error);
		reporter(id, error, attributes);
	};

	return (
		<ErrorBoundaryBase {...props} onError={handleError} FallbackComponent={FallbackComponent}>
			{children}
		</ErrorBoundaryBase>
	);
}
